import {
  REQUEST_DATE_FORMAT,
  useAuth,
  usePortfolio,
  usePortfolioComparison,
  useWhatIfPositions,
  useWhatIfScenario,
} from "@iliotech/data-wire";
import { Typography } from "@progress/kendo-react-common";
import { observer } from "mobx-react-lite";
import {
  BOTTOM_BAR_HEIGHT,
  BottomBar,
  FullScreenCssSpinner,
  RoundButton,
} from "src/main";
import WhatIfStepper from "src/components/__stepTrackers/WhatIfStepper/WhatIfStepper";
import { WhatIfResultsTable } from "src/components/__tables/WhatIfResultsTable/WhatIfResultsTable";
import s from "./WhatIfresultsPage.module.scss";
import {
  EsgComparator,
  IncomeComparator,
  NetWorthComparator,
  PerformanceComparator,
  RiskComparator,
} from "./comparators";
import moment from "moment";
import { SaveScenarioModal } from "src/components/__modals/SaveScenarioModal/SaveScenarioModal";
import { PortfolioComparisonResponse } from "@iliotech/generated-api-v3";
import { PositionComparator } from "./comparators/PositionComparator/PositionComparator";
import { PDFExport } from "@progress/kendo-react-pdf";
import React from "react";
interface WhatIfResultsPageProps {
  onSave: (name: string) => void;
  onDelete: () => void;
  onNotSave: () => void;
  isLoading: boolean;
  scenarioData: PortfolioComparisonResponse;
  virtualId: string;
  basePortfolioId: string;
  title?: string;
  skipCloseModal?: boolean;
  onCloseWithNoSave?: () => void;
}

export const WhatIfResultsPage = observer(
  ({
    onSave,
    isLoading,
    scenarioData,
    virtualId,
    basePortfolioId,
    skipCloseModal,
    title = "What-If",
    onNotSave,
    onCloseWithNoSave,
  }: WhatIfResultsPageProps) => {
    const portfolioData = usePortfolio(basePortfolioId!)?.data?.data;
    const baseCurrency = portfolioData?.currencySymbol || "€";
    const pdfExportComponent = React.useRef<PDFExport>(null);
    const valueFormatter = (v: number) =>
      baseCurrency +
      " " +
      v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    const positionValueFormatter = (v: number, dp = 0, hideCurrency = false) =>
      (hideCurrency ? "" : baseCurrency) +
      " " +
      v.toLocaleString(undefined, { maximumFractionDigits: dp });

    const period = {
      from: portfolioData?.inceptionDate,
      to: moment(moment(new Date()).toDate()).format(REQUEST_DATE_FORMAT),
    };
    const store = useWhatIfPositions();
    const loading = store.transitionLoading || isLoading;

    const onExportPDF = () => {
      if (pdfExportComponent.current) {
        pdfExportComponent.current.save();
      }
    };
    const onFinish = () => {
      if (skipCloseModal || store.skipSave) {
        onCloseWithNoSave?.();
        store.skipSave = false;
        return;
      } else {
        store.saveModal = true;
      }
    };

    const SaveScenarioModalWrapper = observer(() => {
      const store = useWhatIfPositions();

      return (
        <SaveScenarioModal
          onSave={onSave}
          buttonLoading={store.loading}
          handleDelete={onNotSave}
          handleClose={() => (store.saveModal = false)}
          visible={store.saveModal}
        />
      );
    });

    const renderHeader = (title: string) => {
      const isSegments = title === "Segments";
      if (isSegments) return null;
      const isYourPortfolio = title === "Your Portfolio";
      const isWhatIfPortfolio = title === "What if Portfolio";

      if (isYourPortfolio)
        return (
          <div style={{ textAlign: "right", textDecoration: "underline" }}>
            <a target="_blank" href={`/portfolio/${basePortfolioId}`}>
              {title}
            </a>
          </div>
        );
      else if (isWhatIfPortfolio)
        return (
          <div style={{ textAlign: "right", textDecoration: "underline" }}>
            <a target="_blank" href={`/portfolio/${virtualId}`}>
              {title}
            </a>
          </div>
        );
      return <div style={{ textAlign: "right" }}>{title}</div>;
    };
    const renderInner = () => {
      if (loading)
        return (
          <FullScreenCssSpinner
            text="Your What-if Scenario is being calculated"
            footerText={"Your results will be ready \n" + "in a few seconds"}
            containerProps={{ style: { marginTop: 80 } }}
          />
        );
      return (
        <PDFExport
          paperSize={"A4"}
          scale={0.7}
          landscape={true}
          forcePageBreak=".page-break"
          keepTogether=".keep-together"
          ref={pdfExportComponent}
          fileName={`Report - ${moment().format("DD-MM-YYYY")}`}
          author="Portfolio Analytics Team"
        >
          <div
            style={{
              margin: "0 30px 0 30px",
              width: 1100,
              margin: "auto",
            }}
          >
            <div className={s.tableContainer}>
              <h3>Summary</h3>
              <WhatIfResultsTable
                data={scenarioData?.results!}
                baseCurrencySymbol="€"
                headerCellRender={(v, props) => renderHeader(props.title!)}
              />
            </div>
            <div className={"keep-together"}>
              <NetWorthComparator
                virtualId={virtualId}
                basePortfolioId={basePortfolioId!}
                valueFormatter={valueFormatter}
              />
            </div>
            <div style={{ height: 20 }} className={"page-break"} />
            <div className={"keep-together"}>
              <PerformanceComparator
                virtualId={virtualId}
                basePortfolioId={basePortfolioId!}
                period={period as any}
              />
            </div>
            <div style={{ height: 20 }} className={"page-break"} />
            <IncomeComparator
              virtualId={virtualId}
              basePortfolioId={basePortfolioId!}
              valueFormatter={valueFormatter}
            />

            <div style={{ height: 20 }} className={"page-break"} />
            <RiskComparator
              virtualId={virtualId}
              basePortfolioId={basePortfolioId!}
              period={period}
            />
            <div style={{ height: 20 }} className={"page-break"} />
            <EsgComparator
              virtualId={virtualId}
              basePortfolioId={basePortfolioId!}
            />
            <div style={{ height: 20 }} className={"page-break"} />
            <PositionComparator
              virtualId={virtualId}
              basePortfolioId={basePortfolioId!}
              valueFormatter={positionValueFormatter}
            />
          </div>
        </PDFExport>
      );
    };

    return (
      <div className={"container"}>
        <Typography.h1
          fontWeight={"light"}
          textAlign={"center"}
          style={{ marginTop: "5px" }}
        >
          {title}
        </Typography.h1>
        <SaveScenarioModalWrapper />
        {renderInner()}
        <div style={{ paddingBottom: BOTTOM_BAR_HEIGHT }} />
        {
          <BottomBar height={BOTTOM_BAR_HEIGHT} absolute>
            <WhatIfStepper
              currentStep={2}
              edit={false}
              rightContent={
                <div style={{ display: "flex", gap: 10 }}>
                  <RoundButton onClick={onExportPDF} style={{ minWidth: 100 }}>
                    Export PDF
                  </RoundButton>
                  <RoundButton onClick={onFinish} style={{ minWidth: 100 }}>
                    Finish
                  </RoundButton>
                </div>
              }
            />
          </BottomBar>
        }
      </div>
    );
  }
);

export default WhatIfResultsPage;
